import { FeatureFlag } from "../auth/planProvider/PlanProvider.types";
import { getEnumValues } from "@common/enumOperations";
import { PermissionListType } from "../roles/rolePermissionDialog/RolePermissionDialog.types";
import { NavigationMenuItem, NavigationMenuItemDefinition } from "./NavigationMenuSettings.types";

const alwaysAvailable = () => true;

// A Record so a new NavigationMenuItem does not compile until it has a label, a place in the settings dialog
// and a decision on which role permission area it belongs to.
export const navigationMenuItemDefinitions: Record<NavigationMenuItem, NavigationMenuItemDefinition> = {
    [NavigationMenuItem.Dashboard]: {
        labelKey: "dashboard",
        permissionListType: null,
        isAvailable: (planFeatures) => planFeatures.platformFeatures.dashboard || planFeatures.contractManagementFeatures.hasContractManagement,
    },
    [NavigationMenuItem.Frameworks]: { labelKey: "frameworks", permissionListType: PermissionListType.Frameworks, isAvailable: alwaysAvailable },
    [NavigationMenuItem.Processes]: { labelKey: "processes", permissionListType: null, isAvailable: alwaysAvailable },
    [NavigationMenuItem.ProcessTemplates]: {
        labelKey: "processes",
        permissionListType: PermissionListType.Processes,
        isAvailable: (_, isFeatureFlagEnabled) => isFeatureFlagEnabled(FeatureFlag.ProcessTemplates),
    },
    [NavigationMenuItem.Systems]: { labelKey: "systems", permissionListType: PermissionListType.Systems, isAvailable: alwaysAvailable },
    [NavigationMenuItem.Risk]: { labelKey: "risk", permissionListType: PermissionListType.Risk, isAvailable: alwaysAvailable },
    [NavigationMenuItem.Contracts]: {
        labelKey: "contracts",
        permissionListType: PermissionListType.Contracts,
        isAvailable: (planFeatures) => planFeatures.contractManagementFeatures.hasContractManagement,
    },
    [NavigationMenuItem.TaskManagement]: {
        labelKey: "taskManagement",
        permissionListType: null,
        isAvailable: (planFeatures) => planFeatures.platformFeatures.annualWheelOverview,
    },
    [NavigationMenuItem.Documentation]: { labelKey: "documentation", permissionListType: null, isAvailable: alwaysAvailable },
    [NavigationMenuItem.LegalEntities]: { labelKey: "legalEntities", permissionListType: null, isAvailable: alwaysAvailable },
    [NavigationMenuItem.AuditsAndAssessments]: {
        labelKey: "auditsAndAssessments",
        permissionListType: null,
        isAvailable: (planFeatures) => planFeatures.vendorManagementFeatures.hasVendorManagement,
    },

    [NavigationMenuItem.DashboardOverview]: {
        labelKey: "overview",
        parent: NavigationMenuItem.Dashboard,
        permissionListType: null,
        isAvailable: (planFeatures) => planFeatures.platformFeatures.dashboard,
    },
    [NavigationMenuItem.ContractDashboard]: {
        labelKey: "contracts",
        parent: NavigationMenuItem.Dashboard,
        permissionListType: PermissionListType.Contracts,
        isAvailable: (planFeatures, isFeatureFlagEnabled) =>
            isFeatureFlagEnabled(FeatureFlag.ContractDashboard) && planFeatures.contractManagementFeatures.hasContractManagement,
    },

    [NavigationMenuItem.ProcessingActivities]: {
        labelKey: "processingActivities",
        parent: NavigationMenuItem.Processes,
        permissionListType: PermissionListType.ProcessingActivity,
        isAvailable: alwaysAvailable,
    },
    [NavigationMenuItem.Record]: {
        labelKey: "record",
        parent: NavigationMenuItem.Processes,
        permissionListType: PermissionListType.Record,
        isAvailable: alwaysAvailable,
    },

    [NavigationMenuItem.Tasks]: {
        labelKey: "tasks",
        parent: NavigationMenuItem.TaskManagement,
        permissionListType: PermissionListType.TaskManagement,
        isAvailable: alwaysAvailable,
    },
    [NavigationMenuItem.AnnualWheel]: {
        labelKey: "annualWheel",
        parent: NavigationMenuItem.TaskManagement,
        permissionListType: PermissionListType.AnnualWheel,
        isAvailable: (_, isFeatureFlagEnabled) => !isFeatureFlagEnabled(FeatureFlag.HideAnnualWheel),
    },
    [NavigationMenuItem.Activities]: {
        labelKey: "activities",
        parent: NavigationMenuItem.TaskManagement,
        permissionListType: PermissionListType.AnnualWheel,
        isAvailable: (_, isFeatureFlagEnabled) => isFeatureFlagEnabled(FeatureFlag.Activities),
    },

    [NavigationMenuItem.Policies]: {
        labelKey: "policies",
        parent: NavigationMenuItem.Documentation,
        permissionListType: PermissionListType.Policies,
        isAvailable: alwaysAvailable,
    },
    [NavigationMenuItem.IncidentLog]: {
        labelKey: "incidentLog",
        parent: NavigationMenuItem.Documentation,
        permissionListType: PermissionListType.IncidentLog,
        isAvailable: alwaysAvailable,
    },

    [NavigationMenuItem.ExternalLegalEntities]: {
        labelKey: "external",
        parent: NavigationMenuItem.LegalEntities,
        permissionListType: PermissionListType.LegalEntities,
        isAvailable: alwaysAvailable,
    },
    [NavigationMenuItem.GroupEntities]: {
        labelKey: "groupCompanies",
        parent: NavigationMenuItem.LegalEntities,
        permissionListType: PermissionListType.LegalEntities,
        isAvailable: alwaysAvailable,
    },

    [NavigationMenuItem.ExternalAudits]: {
        labelKey: "externalAudits",
        parent: NavigationMenuItem.AuditsAndAssessments,
        permissionListType: PermissionListType.VendorManagement,
        isAvailable: alwaysAvailable,
    },
    [NavigationMenuItem.InternalAssessments]: {
        labelKey: "internalAssessments",
        parent: NavigationMenuItem.AuditsAndAssessments,
        permissionListType: PermissionListType.VendorManagement,
        isAvailable: alwaysAvailable,
    },
};

// The enum is declared in menu order.
const allNavigationMenuItems = getEnumValues(NavigationMenuItem);

// With the new processing activity page the menu shows a single processing activities item where the Processes
// group used to be, so it takes the group's place rather than its declaration order at the end.
// SIMPLIFY when FeatureFlag.NewProcessingActivity is removed - make ProcessingActivities a top-level item in Processes' place and delete Processes and Record
// Until the contract dashboard is released the menu keeps the single dashboard item, which is the overview, where the
// Dashboard group goes.
// SIMPLIFY when FeatureFlag.ContractDashboard is removed - drop the DashboardOverview swap here and in getNavigationMenuItemLabelKey
export function getTopLevelNavigationMenuItems(isFeatureFlagEnabled: (featureFlag: string) => boolean) {
    const topLevelItems = allNavigationMenuItems.filter((item) => navigationMenuItemDefinitions[item].parent === undefined);
    const hasNewProcessingActivity = isFeatureFlagEnabled(FeatureFlag.NewProcessingActivity);
    const hasContractDashboard = isFeatureFlagEnabled(FeatureFlag.ContractDashboard);

    return topLevelItems.map((item) => {
        if (hasNewProcessingActivity && item === NavigationMenuItem.Processes) {
            return NavigationMenuItem.ProcessingActivities;
        }

        if (!hasContractDashboard && item === NavigationMenuItem.Dashboard) {
            return NavigationMenuItem.DashboardOverview;
        }

        return item;
    });
}

// SIMPLIFY when FeatureFlag.ContractDashboard is removed - read labelKey directly
export function getNavigationMenuItemLabelKey(item: NavigationMenuItem, isFeatureFlagEnabled: (featureFlag: string) => boolean) {
    if (item === NavigationMenuItem.DashboardOverview && !isFeatureFlagEnabled(FeatureFlag.ContractDashboard)) {
        return navigationMenuItemDefinitions[NavigationMenuItem.Dashboard].labelKey;
    }

    return navigationMenuItemDefinitions[item].labelKey;
}

export function getNavigationMenuSubItems(parent: NavigationMenuItem) {
    return allNavigationMenuItems.filter((item) => navigationMenuItemDefinitions[item].parent === parent);
}

// A parent with sub items is never saved as hidden; the menu drops it once none of its sub items is shown.
export function isNavigationMenuItemHidden(hiddenItems: Array<NavigationMenuItem>, item: NavigationMenuItem) {
    return hiddenItems.includes(item);
}

export function getHiddenNavigationMenuItemsInPermissionArea(hiddenItems: Array<NavigationMenuItem>, permissionListType: PermissionListType) {
    return allNavigationMenuItems.filter(
        (item) => navigationMenuItemDefinitions[item].permissionListType === permissionListType && isNavigationMenuItemHidden(hiddenItems, item)
    );
}
